import React, { useMemo } from 'react';
import moment from 'moment';
import { Grid, IconButton, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import {
  AccountData,
  AreaOccupancyData,
  DayOccupancyData,
  OrderData,
  SchedulerConfigOccupancyData,
  SchedulerConfigurationData,
  UnavailabilityData,
} from '../../../api';
import { holdForConfig, isManuallySeated } from '../../utils/orderSeating';
import { arrivalsBySlot, AreaSeverity, horizon, severityAt, slotAt, slotIndexOf } from '../utils/occupancyMath';
import { areaDayBounds, areaUsesTicketWording, primaryPageForArea, sessionSlotsForArea } from '../utils/workingHours';
import { blocksForArea } from '../utils/areaBlocks';
import { AREAS_PALETTE } from '../areasPalette';
import { HIDDEN_SCROLLBAR_SX } from '../agendaStyles';
import { AreaUnitWords, formatSeconds } from '../utils/areasFormat';
import { localizedLabel } from '../../../utils/maskUtils';
import { AreaPillarHeader } from './AreaPillarHeader';
import { AreaBlockChips } from './AreaBlockChips';
import { AreaTimelineStrip } from './AreaTimelineStrip';
import { AreaSquare } from './AreaSquare';
import { BlockAreaTarget } from './BlockAreaDialog';

type AvailabilityStripProps = {
  occupancy: DayOccupancyData;
  configs: SchedulerConfigurationData[];
  account: AccountData;
  /** Unfiltered day orders, for the arrivals per slot. */
  dayOrders: OrderData[];
  dayUnavailabilities: UnavailabilityData[];
  selectedSeconds: number;
  nowSeconds: number | null;
  isLive: boolean;
  currentDate: string;
  timeFormat: string;
  language: string;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  /** Area whose orders the list is narrowed to; a square tap toggles it. */
  areaFilter: string | null;
  onToggleAreaFilter: (areaId: string) => void;
  /** Explicit pin plus scroll request, wired in AgendaDayView. */
  onPinTime: (seconds: number) => void;
  /** A timeline cell was clicked: offer what to create at that time in that area. */
  onOpenSlotActions?: (seconds: number, cell: HTMLElement, target: BlockAreaTarget, areaLabel: string) => void;
  canManageBlocks: boolean;
  onManageBlocks: (target: BlockAreaTarget) => void;
  isDropEnabled: boolean;
};

type StripArea = {
  key: string;
  area: AreaOccupancyData;
  occupancyConfig: SchedulerConfigOccupancyData;
  config: SchedulerConfigurationData | undefined;
  slotMinutes: number;
  selectedSlot: number;
  slotData: ReturnType<typeof slotAt>;
  severity: AreaSeverity;
  sessionSlots: number;
  horizonResult: ReturnType<typeof horizon>;
  unitWords: AreaUnitWords;
  swatchColor: string;
  arrivals: Record<number, number>;
  startSlot: number;
  endSlot: number;
  label: string;
};

/**
 * Per-area availability above the list: one square per area for the viewed slot, expandable into the
 * full-day timeline per area. Never filtered by the list's search or chips.
 */
export const AvailabilityStrip = ({
  occupancy,
  configs,
  account,
  dayOrders,
  dayUnavailabilities,
  selectedSeconds,
  nowSeconds,
  isLive,
  currentDate,
  timeFormat,
  language,
  isExpanded,
  onToggleExpanded,
  areaFilter,
  onToggleAreaFilter,
  onPinTime,
  onOpenSlotActions,
  canManageBlocks,
  onManageBlocks,
  isDropEnabled,
}: AvailabilityStripProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const stripAreas = useMemo(
    (): StripArea[] =>
      occupancy.scheduler_configs.flatMap((occupancyConfig) => {
        const config = configs.find((candidate) => candidate.id === occupancyConfig.scheduler_config_id);
        const { slot_minutes: slotMinutes } = occupancyConfig;
        const slotsPerDay = Math.floor((24 * 60) / slotMinutes);
        const pageIdsOfConfig = new Set((config?.allowed_masks ?? []).map((mask) => mask.order_page_id));
        const ordersOfConfig = dayOrders.filter(
          (order) =>
            holdForConfig(order, occupancyConfig.scheduler_config_id) !== undefined ||
            (pageIdsOfConfig.has(order.order_page_id) && !isManuallySeated(order)),
        );

        return occupancyConfig.areas.map((area) => {
          const selectedSlot = slotIndexOf(selectedSeconds, slotMinutes);
          const sessionSlots = sessionSlotsForArea(area, config, account, slotMinutes);
          const usesTickets = areaUsesTicketWording(area, config);
          const bounds = areaDayBounds(area, config, account, currentDate);
          const startSlot = bounds ? slotIndexOf(bounds.startSeconds, slotMinutes) : 0;
          const endSlot = bounds ? slotIndexOf(bounds.endSeconds - 1, slotMinutes) : slotsPerDay - 1;
          return {
            key: `${occupancyConfig.scheduler_config_id}-${area.area_id}`,
            area,
            occupancyConfig,
            config,
            slotMinutes,
            selectedSlot,
            slotData: slotAt(area, selectedSlot),
            severity: severityAt(slotAt(area, selectedSlot)),
            sessionSlots,
            horizonResult: horizon({ area, fromSlot: selectedSlot, sessionSlots, dayEndSlot: endSlot, slotMinutes }),
            unitWords: {
              plural: usesTickets ? t('calendar.areas_view.unit.stations') : t('calendar.areas_view.unit.tables'),
              singular: usesTickets ? t('calendar.areas_view.unit.station') : t('calendar.areas_view.unit.table'),
              statLabel: usesTickets ? t('calendar.areas_view.unit.projects') : t('calendar.areas_view.free_label'),
            },
            swatchColor: primaryPageForArea(area, config, account)?.color || AREAS_PALETTE.defaultPageColor,
            arrivals: config ? arrivalsBySlot(ordersOfConfig, area, config, slotMinutes) : {},
            startSlot,
            endSlot,
            label: localizedLabel(area.label, language, area.area_id),
          };
        });
      }),
    [occupancy, configs, account, dayOrders, selectedSeconds, currentDate, language, t],
  );

  /**
   * One time axis for every area, the union of their hours: strips that each span only their own
   * hours have cells of different widths, so the eye cannot compare areas at the same time of day.
   * An area's hours outside the union draw as closed cells instead.
   */
  const axisStartSlot = stripAreas.length > 0 ? Math.min(...stripAreas.map((stripArea) => stripArea.startSlot)) : 0;
  const axisEndSlot = stripAreas.length > 0 ? Math.max(...stripAreas.map((stripArea) => stripArea.endSlot)) : 0;

  const showConfigLabel = occupancy.scheduler_configs.length > 1;

  return (
    <Paper variant="outlined" sx={{ borderRadius: '8px', padding: 1, flexShrink: 0 }}>
      <Grid container wrap="nowrap" alignItems="center" gap={isSmallScreen ? 1 : 1.25}>
        <Grid
          sx={
            isSmallScreen
              ? { flex: 1, minWidth: 0, display: 'flex', gap: 1, overflowX: 'auto', ...HIDDEN_SCROLLBAR_SX }
              : {
                  flex: 1,
                  minWidth: 0,
                  display: 'grid',
                  gridTemplateColumns: `repeat(${Math.max(stripAreas.length, 1)}, minmax(0, 1fr))`,
                  gap: 1.25,
                }
          }>
          {stripAreas.map(({ key, area, occupancyConfig, label, slotData, severity, arrivals, selectedSlot }) => (
            <AreaSquare
              key={key}
              schedulerConfigId={occupancyConfig.scheduler_config_id}
              area={area}
              label={label}
              slotData={slotData}
              severity={severity}
              arrivalsInSlot={arrivals[selectedSlot] ?? 0}
              isFiltered={areaFilter === area.area_id}
              isDropEnabled={isDropEnabled}
              onToggleFilter={() => onToggleAreaFilter(area.area_id)}
            />
          ))}
        </Grid>

        <Grid
          container
          direction={isSmallScreen ? 'row' : 'column'}
          alignItems="center"
          justifyContent="center"
          wrap="nowrap"
          sx={{ width: isSmallScreen ? 'auto' : '84px', flexShrink: 0 }}>
          {!isSmallScreen && (
            <>
              {/* The time the squares answer for. It is the one number the whole strip hangs on. */}
              <Typography
                dir="ltr"
                sx={{
                  fontSize: isSmallScreen ? '16px' : '24px',
                  fontWeight: isSmallScreen ? 700 : 800,
                  lineHeight: 1.1,
                  fontVariantNumeric: 'tabular-nums',
                  color: isLive ? 'mb.terracotta' : 'text.primary',
                }}>
                {formatSeconds(selectedSeconds, timeFormat)}
              </Typography>
              {/* The date carries the same weight as the time: staff switch days often and must not misread which one.
              On a phone the toolbar above already shows the date, so the strip keeps only the time. */}
              {!isSmallScreen && (
                <>
                  <Typography
                    dir="ltr"
                    sx={{
                      fontSize: '24px',
                      fontWeight: 800,
                      lineHeight: 1.1,
                      fontVariantNumeric: 'tabular-nums',
                      color: 'text.primary',
                      whiteSpace: 'nowrap',
                    }}>
                    {moment(currentDate).format('D.M')}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {moment(currentDate).format('dddd')}
                  </Typography>
                </>
              )}
            </>
          )}
          <IconButton
            size="small"
            sx={isSmallScreen ? { width: '36px', height: '36px' } : undefined}
            onClick={onToggleExpanded}
            aria-label={isExpanded ? t('calendar.areas_view.strip_collapse') : t('calendar.areas_view.strip_expand')}>
            <ExpandMoreIcon sx={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }} />
          </IconButton>
        </Grid>
      </Grid>

      {isExpanded && (
        <Grid
          container
          direction="column"
          wrap="nowrap"
          gap={1}
          sx={{ marginTop: 1, maxHeight: { xs: '50vh', md: 'none' }, overflowY: 'auto', ...HIDDEN_SCROLLBAR_SX }}>
          {stripAreas.map((stripArea, index) => {
            const { key, area, occupancyConfig, config, slotMinutes, slotData, severity, sessionSlots } = stripArea;
            const isFirstOfConfig =
              index === 0 ||
              stripAreas[index - 1].occupancyConfig.scheduler_config_id !== occupancyConfig.scheduler_config_id;
            return (
              <Grid key={key}>
                {showConfigLabel && isFirstOfConfig && (
                  <Typography sx={{ fontSize: '12px', color: 'text.secondary', marginBottom: 0.5 }}>
                    {localizedLabel(occupancyConfig.label, language)}
                  </Typography>
                )}
                <Paper variant="outlined" sx={{ borderRadius: '8px', overflow: 'hidden' }}>
                  <AreaPillarHeader
                    areaLabel={stripArea.label}
                    swatchColor={stripArea.swatchColor}
                    slotData={slotData}
                    severity={severity}
                    horizon={stripArea.horizonResult}
                    unitWords={stripArea.unitWords}
                    sessionMinutes={sessionSlots * slotMinutes}
                    timeFormat={timeFormat}
                    canManageBlocks={canManageBlocks}
                    onManageBlocks={() => onManageBlocks({ area, occupancyConfig, config })}
                  />
                  <Grid container direction="column" wrap="nowrap" sx={{ paddingInline: 1.5 }}>
                    <AreaBlockChips
                      blocks={blocksForArea(dayUnavailabilities, occupancyConfig, area)}
                      area={area}
                      selectedSeconds={selectedSeconds}
                      timeFormat={timeFormat}
                      onManage={canManageBlocks ? () => onManageBlocks({ area, occupancyConfig, config }) : undefined}
                    />
                  </Grid>
                  <AreaTimelineStrip
                    area={area}
                    slotMinutes={slotMinutes}
                    arrivals={stripArea.arrivals}
                    startSlot={axisStartSlot}
                    endSlot={axisEndSlot}
                    openStartSlot={stripArea.startSlot}
                    openEndSlot={stripArea.endSlot}
                    selectedSlot={stripArea.selectedSlot}
                    nowSlot={nowSeconds === null ? null : slotIndexOf(nowSeconds, slotMinutes)}
                    timeFormat={timeFormat}
                    onSlotClick={(seconds, cell) => {
                      onPinTime(seconds);
                      onOpenSlotActions?.(seconds, cell, { area, occupancyConfig, config }, stripArea.label);
                    }}
                  />
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Paper>
  );
};
